import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area, LineChart, Line
} from "recharts";
import { DollarSign, Users, Plane, TrendingUp, Home, CreditCard } from "lucide-react";

const COLORS = ["#10b981","#3b82f6","#f59e0b","#ef4444","#8b5cf6","#ec4899","#06b6d4","#84cc16"];
const AIRPORT_COLORS = { "O'Hare (ORD)": "#3b82f6", "Midway (MDW)": "#f59e0b", "Milwaukee (MKE)": "#10b981", "Madison (MSN)": "#8b5cf6" };

function MetricCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl border p-5 flex items-center gap-4" data-testid={`metric-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function ChartCard({ title, children, className = "" }) {
  return (
    <Card className={`shadow-sm border rounded-2xl ${className}`}>
      <CardHeader className="pb-2 pt-4 px-5">
        <CardTitle className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-4">{children}</CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg rounded-xl border p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' && p.name.toLowerCase().includes('revenue') ? `$${p.value.toFixed(0)}` : p.value}
        </p>
      ))}
    </div>
  );
};

export function AnalyticsTab({ bookings = [] }) {
  const analytics = useMemo(() => {
    const now = new Date();
    const totalRevenue = bookings.reduce((s, b) => s + (b.amount_paid || 0), 0);
    const pendingRevenue = bookings.reduce((s, b) => s + (b.balance_due || 0), 0);
    const totalPax = bookings.reduce((s, b) => s + (b.num_passengers || 1), 0);
    const confirmed = bookings.filter(b => b.booking_status === "confirmed").length;
    const pending = bookings.filter(b => b.booking_status === "pending").length;
    const cancelled = bookings.filter(b => b.booking_status === "cancelled").length;
    const arrivals = bookings.filter(b => b.trip_type === "arrival").length;
    const returns = bookings.filter(b => b.trip_type === "return").length;

    // Daily revenue (last 14 days)
    const dailyMap = {};
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now); d.setDate(d.getDate() - i);
      const key = d.toISOString().split("T")[0];
      dailyMap[key] = { date: `${d.getMonth()+1}/${d.getDate()}`, collected: 0, pending: 0, passengers: 0, bookings: 0 };
    }
    bookings.forEach(b => {
      const d = b.travel_date?.split("T")[0];
      if (dailyMap[d]) {
        dailyMap[d].collected += (b.amount_paid || 0);
        dailyMap[d].pending += (b.balance_due || 0);
        dailyMap[d].passengers += (b.num_passengers || 1);
        dailyMap[d].bookings += 1;
      }
    });
    const dailyRevenue = Object.values(dailyMap);

    // Airport distribution
    const airportMap = {};
    bookings.forEach(b => {
      const ap = b.pickup_location || b.dropoff_location || "Unknown";
      airportMap[ap] = (airportMap[ap] || 0) + 1;
    });
    const airportData = Object.entries(airportMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Payment method breakdown
    const payMap = {};
    bookings.forEach(b => {
      const m = b.payment_method || "unknown";
      const label = m === "stripe" ? "Stripe" : m === "cash" ? "Cash" : m === "zelle" ? "Zelle" : m === "manual" ? "Manual" : m;
      payMap[label] = (payMap[label] || 0) + 1;
    });
    const paymentData = Object.entries(payMap).map(([name, value]) => ({ name, value }));

    // Booking status
    const statusData = [
      { name: "Confirmed", value: confirmed, color: "#10b981" },
      { name: "Pending", value: pending, color: "#f59e0b" },
      { name: "Cancelled", value: cancelled, color: "#ef4444" },
    ].filter(d => d.value > 0);

    // Trip type
    const tripData = [
      { name: "Arrival", value: arrivals, color: "#3b82f6" },
      { name: "Return", value: returns, color: "#8b5cf6" },
    ].filter(d => d.value > 0);

    // Housing distribution (top 8)
    const housingMap = {};
    bookings.forEach(b => {
      const h = b.housing_unit || b.housing_address || "N/A";
      if (h && h !== "N/A") housingMap[h] = (housingMap[h] || 0) + (b.num_passengers || 1);
    });
    const housingData = Object.entries(housingMap)
      .map(([name, pax]) => ({ name: name.length > 18 ? name.slice(0, 16) + "..." : name, pax }))
      .sort((a, b) => b.pax - a.pax)
      .slice(0, 8);

    // Weekly trend (last 8 weeks)
    const weekMap = {};
    for (let i = 7; i >= 0; i--) {
      const d = new Date(now); d.setDate(d.getDate() - i * 7);
      const weekStart = new Date(d); weekStart.setDate(d.getDate() - d.getDay());
      const key = weekStart.toISOString().split("T")[0];
      const label = `${weekStart.getMonth()+1}/${weekStart.getDate()}`;
      weekMap[key] = { week: label, bookings: 0, revenue: 0 };
    }
    bookings.forEach(b => {
      const bd = new Date(b.travel_date);
      const weekStart = new Date(bd); weekStart.setDate(bd.getDate() - bd.getDay());
      const key = weekStart.toISOString().split("T")[0];
      if (weekMap[key]) {
        weekMap[key].bookings += 1;
        weekMap[key].revenue += (b.amount_paid || 0);
      }
    });
    const weeklyTrend = Object.values(weekMap);

    return { totalRevenue, pendingRevenue, totalPax, confirmed, pending, cancelled, dailyRevenue, airportData, paymentData, statusData, tripData, housingData, weeklyTrend, arrivals, returns };
  }, [bookings]);

  const renderLabel = ({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : "";

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Top Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={DollarSign} label="Revenue Collected" value={`$${analytics.totalRevenue.toFixed(0)}`} sub={`$${analytics.pendingRevenue.toFixed(0)} pending`} color="bg-emerald-500" />
        <MetricCard icon={Users} label="Total Passengers" value={analytics.totalPax} sub={`${bookings.length} bookings`} color="bg-blue-500" />
        <MetricCard icon={Plane} label="Arrivals" value={analytics.arrivals} sub={`${analytics.returns} returns`} color="bg-violet-500" />
        <MetricCard icon={TrendingUp} label="Confirmed" value={analytics.confirmed} sub={`${analytics.pending} pending · ${analytics.cancelled} cancelled`} color="bg-amber-500" />
      </div>

      {/* Row 1: Revenue + Passengers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Daily Revenue (Last 14 Days)">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={analytics.dailyRevenue} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="collected" name="Collected Revenue" fill="#10b981" radius={[4,4,0,0]} />
              <Bar dataKey="pending" name="Pending Revenue" fill="#f59e0b" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Daily Passengers (Last 14 Days)">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={analytics.dailyRevenue} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="paxGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="passengers" name="Passengers" stroke="#3b82f6" fill="url(#paxGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="bookings" name="Bookings" stroke="#8b5cf6" fill="none" strokeWidth={2} strokeDasharray="5 5" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 2: Pies */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ChartCard title="Airport Distribution">
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={analytics.airportData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={renderLabel}>
                {analytics.airportData.map((entry, i) => (
                  <Cell key={i} fill={AIRPORT_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Payment Methods">
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={analytics.paymentData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={renderLabel}>
                {analytics.paymentData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Booking Status">
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={analytics.statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={renderLabel}>
                {analytics.statusData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 3: Housing + Weekly Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top Housing Pickups (by Passengers)">
          {analytics.housingData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics.housingData} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} />
                <Tooltip />
                <Bar dataKey="pax" name="Passengers" fill="#8b5cf6" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground py-12 text-center">No housing data yet</p>
          )}
        </ChartCard>

        <ChartCard title="Weekly Booking Trend (Last 8 Weeks)">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={analytics.weeklyTrend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" height={30} iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="left" type="monotone" dataKey="bookings" name="Bookings" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
              <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 4: Arrivals vs Returns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Trip Type Split">
          <div className="flex items-center gap-8 justify-center py-6">
            {analytics.tripData.map(t => (
              <div key={t.name} className="text-center">
                <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-2" style={{ background: `${t.color}18`, border: `3px solid ${t.color}` }}>
                  <span className="text-2xl font-bold" style={{ color: t.color }}>{t.value}</span>
                </div>
                <p className="text-sm font-medium text-gray-600">{t.name}</p>
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Revenue Breakdown">
          <div className="flex items-center justify-center py-6 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>${analytics.totalRevenue.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Collected</p>
              <div className="w-16 h-1.5 bg-emerald-500 rounded-full mx-auto mt-2"></div>
            </div>
            <div className="text-4xl text-gray-300 font-light">/</div>
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-500" style={{ fontFamily: 'Outfit, sans-serif' }}>${analytics.pendingRevenue.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Pending</p>
              <div className="w-16 h-1.5 bg-amber-400 rounded-full mx-auto mt-2"></div>
            </div>
            <div className="text-4xl text-gray-300 font-light">/</div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600" style={{ fontFamily: 'Outfit, sans-serif' }}>${(analytics.totalRevenue + analytics.pendingRevenue).toFixed(0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Expected</p>
              <div className="w-16 h-1.5 bg-blue-500 rounded-full mx-auto mt-2"></div>
            </div>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
